import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";

const API = "https://api3.geo.admin.ch/rest/services/api/SearchServer";
const strip = (s) => (s || "").replace(/<[^>]+>/g, "").trim();
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const cleanLabel = (r) => strip(r.attrs?.label).replace(/\s*#\s*/g, " ").replace(/\s+/g, " ").trim();
export const relevant = (results, query) => {
  const words = norm(query).split(/[\s,]+/).filter((w) => w.length >= 2);
  const seen = new Set();
  return results.filter((r) => {
    const l = norm(cleanLabel(r));
    if (seen.has(l) || !words.some((w) => l.includes(w))) return false;
    seen.add(l);
    return true;
  });
};

export const parseAddress = (a = "") => {
  const m = a.match(/^(.*?)\s+(\d+\s?[a-zA-Z]?)\s*,\s*(\d{4})\s+(.+)$/);
  if (m) return { street: m[1], number: m[2], npa: m[3], city: m[4] };
  const m2 = a.match(/^(.*?)\s*,\s*(\d{4})\s+(.+)$/);
  return m2 ? { street: m2[1], number: "", npa: m2[2], city: m2[3] } : { street: a, number: "", npa: "", city: "" };
};
export const formatAddress = ({ street, number, npa, city }) =>
  [[street, number].filter(Boolean).join(" "), [npa, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");

export const fromResult = (r) => {
  const a = r.attrs || {};
  const label = a.label || "";
  const loc = strip((label.match(/<b>(.*?)<\/b>/) || [])[1] || "");
  const head = strip(label.split("<b>")[0]).replace(/#/g, "").replace(/\s+/g, " ").trim();
  const num = a.num ? String(a.num) : "";
  const street = num && head.endsWith(num) ? head.slice(0, -num.length).trim() : head;
  const [npa, ...rest] = loc.split(" ");
  const canton = ((a.detail || "").trim().split(" ").pop() || "").toUpperCase();
  return { street, number: num || (head.match(/\s(\d+\w?)$/) || [])[1] || "", npa, city: rest.join(" "), canton: /^[A-Z]{2}$/.test(canton) ? canton : "" };
};

export default function AddressAutocomplete({ value, onChange, testPrefix = "address" }) {
  const { t } = useTranslation();
  const [parts, setParts] = useState(() => parseAddress(value));
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [searched, setSearched] = useState(false);
  const typed = useRef(false);
  const boxRef = useRef(null);

  const query = [parts.street, parts.number, parts.npa, parts.city].filter(Boolean).join(" ");
  useEffect(() => {
    if (!typed.current || query.length < 3) { setItems([]); setSearched(false); return; }
    const ctrl = new AbortController();
    const id = setTimeout(() => {
      fetch(`${API}?type=locations&origins=address&limit=8&searchText=${encodeURIComponent(query)}`, { signal: ctrl.signal })
        .then((r) => r.json()).then((d) => { setItems(relevant(d.results || [], query).slice(0, 8)); setSearched(true); setOpen(true); setActive(-1); })
        .catch(() => {});
    }, 300);
    return () => { clearTimeout(id); ctrl.abort(); };
  }, [query]);
  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const update = (p, canton) => { setParts(p); onChange({ address: formatAddress(p), ...(canton ? { canton } : {}) }); };
  const edit = (k) => (e) => { typed.current = true; update({ ...parts, [k]: e.target.value }); };
  const pick = (r) => { const x = fromResult(r); typed.current = false; setOpen(false); setItems([]); update({ street: x.street, number: x.number, npa: x.npa, city: x.city }, x.canton); };
  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative space-y-2" data-testid={`${testPrefix}-autocomplete`}>
      <div className="grid grid-cols-[1fr_5.5rem] gap-2">
        <input value={parts.street} onChange={edit("street")} onKeyDown={onKey} placeholder={t("address.street")} aria-label={t("address.street")} autoComplete="off"
          role="combobox" aria-expanded={open} aria-controls={`${testPrefix}-list`} className="input-c" data-testid={`${testPrefix}-street`} />
        <input value={parts.number} onChange={edit("number")} onKeyDown={onKey} placeholder={t("address.number")} aria-label={t("address.number")} className="input-c" data-testid={`${testPrefix}-number`} />
      </div>
      <div className="grid grid-cols-[5.5rem_1fr] gap-2">
        <input value={parts.npa} onChange={edit("npa")} onKeyDown={onKey} inputMode="numeric" maxLength={4} placeholder={t("address.npa")} aria-label={t("address.npa")} className="input-c" data-testid={`${testPrefix}-npa`} />
        <input value={parts.city} onChange={edit("city")} onKeyDown={onKey} placeholder={t("address.city")} aria-label={t("address.city")} className="input-c" data-testid={`${testPrefix}-city`} />
      </div>
      {open && searched && (
        <ul id={`${testPrefix}-list`} role="listbox" className="absolute z-50 left-0 right-0 top-12 bg-white border border-c rounded-xl shadow-lg overflow-hidden" data-testid={`${testPrefix}-suggestions`}>
          {items.length === 0 && <li className="px-3 py-2.5 text-sm text-text-secondary" data-testid={`${testPrefix}-no-result`}>{t("address.none")}</li>}
          {items.map((r, i) => (
            <li key={r.id ?? i} role="option" aria-selected={i === active}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(r)} data-testid={`${testPrefix}-suggestion-${i}`}
                className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 ${i === active ? "bg-primary-c/10" : "hover:bg-surface-muted"}`}>
                <MapPin className="w-4 h-4 text-primary-c shrink-0" />{cleanLabel(r)}
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-text-secondary">{t("address.hint")}</p>
    </div>
  );
}
